
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SessionStorage } from 'ngx-webstorage';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DynamicModification } from '../models/dynamic-modification';
import { Locale } from '../models/locale';
import { OperationSource } from '../undo-redo-manager/message-bus/message-types/operation-message';
import { AlertType, EazlyMessage, MessageCause } from '../../tools/services/messaging/eazly-message';
import { MessagingService } from '../../tools/services/messaging/messaging.service';
import { AppeazService } from '../../tools/services/appeaz.service';

@Injectable()
export class ViewerService {
    @SessionStorage('auth.Engine.token')
    public engineToken;

    errorMessage: EazlyMessage;
    users: DynamicModification[];
    appeazService: AppeazService;
    protected httpClient: HttpClient;

    private readonly EAZLY_API_URL = '/eazly-api/v1/pages/';

    constructor(private readonly http: HttpClient,
        private readonly messagingService: MessagingService,
        appeazService: AppeazService) {
        this.httpClient = http;
        this.appeazService = appeazService;
    }

    addDynamicModification(source: string, stepId: string, dynamicModifications: DynamicModification[]): Observable<any> {
        return this.appeazService.getVersionByAppeaz(source).pipe(
            switchMap(apiV => {
                const httpOptions = {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'x-access-token': this.engineToken
                    })
                };

                let dynamicModification;
                switch (apiV) {
                    case '1.3':
                        dynamicModification = dynamicModifications[0];
                        break;
                    case '1.4':
                        dynamicModification = dynamicModifications;
                        break;
                    default:
                        this.errorMessage = new EazlyMessage(
                            AlertType.danger,
                            'UnsupportedAPIVersion',
                            MessageCause.UnsupportedAPIVersion,
                            ''
                        );
                        this.messagingService.notifyError(this.errorMessage);
                        return throwError(new Error('Unsupported API Version'));
                }

                const constructedURL = `${source}${this.EAZLY_API_URL}${stepId}/dynamic-modifications`;
                return this.http.post(constructedURL, dynamicModification, httpOptions).pipe(
                    map(response => response),
                    catchError((err: HttpErrorResponse) => throwError(err))
                );
            })
        );
    }

    /**
     * Method calling the API Rest for editing an existing DM.
     *
     * @param operationSource OperationSource indicating if the request is done from the 'DO', 'UNDO' or 'REDO'
     * @param appeazUrl url of the Appeaz
     * @param id id of the DM to edit
     * @param stepId id of the Step containing the DM to edit
     * @param dynamicModification DM with the values of properties to set
     * @returns an Observable of a DM containing the old values of properties of the DM for building the 'UNDO' operation
     */
    updateDynamicModification(
        operationSource: OperationSource,
        appeazUrl: string,
        id: number,
        stepId: string,
        dynamicModification: DynamicModification): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'x-access-token': this.engineToken,
                'eazly-operation-source': operationSource.toString()
            }),
        };
        const constructedURL = `${appeazUrl}/eazly-api/v1/pages/${stepId}/dynamic-modifications/${id}`;
        return this.http.put(constructedURL, dynamicModification, httpOptions).pipe(map(
            response => response,
            catchError((err: HttpErrorResponse) => throwError(err))
        ));
    }

    getDynamicModification(url: string, stepId: string, dynamicmodificationId: number): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'x-access-token': this.engineToken,
            })
        };

        return this.http.get(`${url}/eazly-api/v1/pages/${stepId}/dynamic-modifications/${dynamicmodificationId}`, httpOptions)
            .pipe(
                map(
                    response => response,
                    catchError((err: HttpErrorResponse) => throwError(err))
                ));
    }

    getDynamicModificationPreviewById(url: string, stepId: string, dynamicmodificationId: number, locale: Locale): Observable<string> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'x-access-token': this.engineToken,
            }), responseType: 'text' as 'text',
        };
        const localeToRequest = this.getLocaleToRequest(locale);
        return this.http.get(`${url}/eazly-api/v1/pages/${stepId}/dynamic-modifications/${dynamicmodificationId}/preview${localeToRequest}`, httpOptions);

    }

    getStepPreviewById(url: string, stepId: string, locale: Locale): Observable<string> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'x-access-token': this.engineToken
            }), responseType: 'text' as 'text',
        };
        const localeToRequest = this.getLocaleToRequest(locale);
        return this.http.get(`${url}/eazly-api/v1/pages/${stepId}/preview${localeToRequest}`, httpOptions);
    }

    deleteDynamicModificationPreviewById(url: string, stepId: string, dynamicmodificationIds: Array<number>, idAppeaz: number, appeazRole: string): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({

                'x-access-token': this.engineToken,
            }),
            body: dynamicmodificationIds
        };
        let requestUrl = `${url}${this.EAZLY_API_URL}${stepId}/dynamic-modifications/`;
        if (dynamicmodificationIds.length === 1) {
            requestUrl += dynamicmodificationIds[0];
        }
        return this.http.delete(requestUrl, httpOptions).pipe(
            map(
                response => response,
                catchError((err: HttpErrorResponse) => throwError(err))
            ));

    }
    getStepDynamicModifications(url: string, stepId: string): Observable<DynamicModification[]> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'x-access-token': this.engineToken,
            })
        };
        return this.http.get(url + this.EAZLY_API_URL + stepId + '/dynamic-modifications', httpOptions).pipe(map(
            response => response['modifications'],
            catchError((err: HttpErrorResponse) => throwError(err))
        ));
    }

    /**
     * Return a suffix to append to API rest URLs for the requested locale with a starting '/'.
     * Ex: '/fr_CA'
     *
     *  If the requested locale is not defined, an empty string will be returned
     *
     * @param locale The requested locale
     * @returns a suffix to append to API rest URLs corresponding to the locale provided
     */
    private getLocaleToRequest(locale: Locale): string {
        let localeToRequest = '';
        if (locale?.languageCode) {
            localeToRequest = localeToRequest.concat('/', locale.languageCode);
            if (locale?.countryCode) {
                localeToRequest = localeToRequest.concat('_', locale.countryCode);
            }
        }
        return localeToRequest;
    }
}
